import {SvgIconComponent} from "@mui/icons-material";
import Button from "./Button";
import React, {useEffect} from "react";
import {DropDown} from "./DropDown";
import _ from "lodash";
import {SelectChangeEvent} from "@mui/material";

export type VeneListActionbarType<T> = {
    text?: string;
    icon: React.ReactElement;
    onClick: (dataEntry: T) => void;
}

export type VeneListColDefinitionType = {
    colName: string;
    keyName: string;
}

interface VeneListProps<T> {
    data: T[];
    buttonConfig?: VeneListActionbarType<T>[];
    colDefinition?: VeneListColDefinitionType[];
    isFilterDisabled?: boolean;
}

type VeneListSortingType = {
    sortingDirection: 'asc' | 'desc';
    sortingField: string;
}

type VeneListFilterType = {
    filterField: string;
    filterText: string | null;
}

type VeneListPageType = {
    pageSize: number;
    currentPage: number;
}

export function VeneList<T extends Object>(props: VeneListProps<T>) {
    const [sorting, setSorting] = React.useState<VeneListSortingType>({
        sortingDirection: 'asc',
        sortingField: props.colDefinition ? props.colDefinition[0].keyName : Object.keys(props.data[0])[0]
    });

    const [filter, setFilter] = React.useState<VeneListFilterType>({
        filterField: props.colDefinition ? props.colDefinition[0].keyName : Object.keys(props.data[0])[0],
        filterText: null
    });

    const [page, setPage] = React.useState<VeneListPageType>({
        pageSize: 50,
        currentPage: 1,
    });

    const [filterDropDownValues, setFilterDropDownValues] = React.useState<{ itemValue: number, itemText: string }[]>([]);

    const [ selectedFilterField, setSelectedFilterField ] = React.useState<{ itemValue: number, itemText: string }>({
        itemValue: 0,
        itemText: '',
    });

    useEffect(() => {
        if (props.colDefinition !== undefined && props.data.length > 0) {
            const dropDownData = [];
            let counter = 0;
            for (const colDefinition of props.colDefinition) {
                if (Object.keys(props.data[0]).includes(colDefinition.keyName)) {
                    dropDownData.push({
                        itemValue: counter,
                        itemText: colDefinition.colName
                    });
                    counter++;
                }
            }

            setFilterDropDownValues(dropDownData);
        }
    }, [props.colDefinition, props.data]);

    const onHeaderClick = (keyName: string) => {
        if (sorting.sortingField === keyName) {
            if (sorting.sortingDirection === 'asc') {
                setSorting({
                    ...sorting,
                    sortingDirection: 'desc',
                })
            } else {
                setSorting({
                    ...sorting,
                    sortingDirection: 'asc'
                })
            }
        } else {
            setSorting({
                sortingField: keyName,
                sortingDirection: 'asc'
            })
        }
    }

    const renderHeader = () => {
        const headerCellHTML = [];
        if (props.colDefinition) {
            for (const colDefinition of props.colDefinition) {
                if (Object.keys(props.data[0]).includes(colDefinition.keyName)) {
                    headerCellHTML.push(
                        <th scope="col" onClick={() => onHeaderClick(colDefinition.keyName) } key={colDefinition.keyName}>{colDefinition.colName}</th>
                    )
                }
            }
        } else {
            Object.keys(props.data[0]).map((colName) => {
                headerCellHTML.push(colName)
            })
        }

        const areButtonsConfigured = props.buttonConfig !== undefined && props.buttonConfig.length > 0;

        return (
            <tr>
                { headerCellHTML }
                { areButtonsConfigured && <th>Aktionen</th> }
            </tr>
        );
    }

    const renderOneRow = (currentObject: T) => {
        const returnHTML = [];

        if (props.colDefinition) {
            let i = 0;
            let j = 0;
            while (i <= Object.entries(currentObject).length - 1) {
                const currentValuePair = Object.entries(currentObject)[i]
                if (currentValuePair[0] === props.colDefinition[j].keyName) {
                    returnHTML.push(
                        <td dangerouslySetInnerHTML={{ __html: currentValuePair[1] }}/>
                    )

                    if (j < props.colDefinition.length - 1) {
                        j++;
                        i = 0;
                    } else {
                        break;
                    }
                }

                i++;
            }
        } else {
            for (const valuePair of Object.entries(currentObject)) {
                if (valuePair[0] !== "__typename") {
                    returnHTML.push (
                        <td>{valuePair[1]}</td>
                    )
                }
            }
        }

        if (props.buttonConfig) {
            const buttonHTMl = []
            for (const button of props.buttonConfig) {
                buttonHTMl.push(
                    <Button onClick={() => button.onClick(currentObject)}>{button.text}{ button.icon }</Button>
                )
            }

            returnHTML.push(<td>{buttonHTMl}</td>)
        }

        return returnHTML;
    }


    const applySorting = (dataSet: T[]) => {
        return dataSet.sort((a: T, b: T) => {
            const firstEntry = Object.entries(a).find((value) => value[0] === sorting.sortingField);
            const secondEntry = Object.entries(b).find((value) => value[0] === sorting.sortingField);
            if (firstEntry !== undefined && secondEntry !== undefined) {
                if (sorting.sortingDirection === 'asc') {
                    if (firstEntry[1] < secondEntry[1]) {
                        return 1
                    } else if (firstEntry[1] > secondEntry[1]) {
                        return -1
                    }
                }
                else {
                    if (firstEntry[1] > secondEntry[1]) {
                        return 1
                    } else if (firstEntry[1] < secondEntry[1]) {
                        return -1
                    }
                }
            }

            return -1;
        })
    }

    const applyFilter = (dataSet: T[]) => {
        if (filter.filterText === null) {
            return dataSet;
        }

        return dataSet.filter((set : T) => {
            const entry = Object.entries(set).find((value) => value[0] === filter.filterField);
            if (entry === undefined) {
                return false;
            }

            if (typeof entry[1] === "string") {
                return entry[1].toLowerCase().includes(filter.filterText!.toLowerCase());
            }

            return entry[1].includes(filter.filterText);
        })
    }

    const applyPagination = (dataSet: T[]) => {
        const startIndex = (page.currentPage - 1) * page.pageSize
        const endIndex = page.currentPage === 1 ? page.pageSize - 1 : page.currentPage * page.pageSize

        return dataSet.slice(startIndex, endIndex);
    }

    const renderBody = () => {
        const sortedData: T[] = applySorting(Array.from(props.data));
        const filteredData: T[] = applyFilter(sortedData);
        const paginatedData: T[] = applyPagination(filteredData);
        const bodyHTML = [];
        let rowIndex = 0;

        for (const currentObject of paginatedData) {
            let alternatingClass = rowIndex % 2 === 0;
            bodyHTML.push(
                <tr key={`table-row-${rowIndex}`} className={ alternatingClass ? "alternate-table-row" : "default-table-row" }>{renderOneRow(currentObject)}</tr>
            )
            rowIndex++;
        }

        return bodyHTML;
    }

    if (props.data.length < 1) {
        return (
            <div>Keine Daten vorhanden</div>
        )
    }

    const setFilterValue = (value: string) => {
        if (value === "") {
            setFilter({
                ...filter,
                filterText: null,
            })
        } else {
            setFilter({
                ...filter,
                filterText: value,
            })
        }
    }

    const setFilterDropDown = (event: SelectChangeEvent) => {
        let chosenFilterField;
        let dropDownText: string | undefined;

        dropDownText = filterDropDownValues.find((item) => item.itemValue === Number(event.target.value))?.itemText
        if (props.colDefinition && dropDownText) {
            chosenFilterField = props.colDefinition.find((item) => item.colName === dropDownText)?.keyName;
        } else {
            chosenFilterField = dropDownText;
        }

        setSelectedFilterField({ itemValue: Number(event.target.value), itemText: dropDownText ?? "" });
        setFilter({
            ...filter,
            filterField: chosenFilterField ?? "",
        })
    }

    return (
        <div className="veneList">
            { props.isFilterDisabled !== true && (
                <div className="veneList-filterbar">
                    <div className="vene-filter-input">
                        <label htmlFor="veneList-text-search">Textsuche:</label>
                        <input id="veneList-text-search" type="text" value={ filter.filterText === null ? "" : filter.filterText } onChange={(e) => setFilterValue(e.target.value)} />
                    </div>
                    <DropDown label="Filter auf: " value={ selectedFilterField.itemValue } setValue={ (event) => setFilterDropDown(event) } items={ filterDropDownValues }/>
                </div>
            ) }
            <table className="veneList-table">
                <thead className="veneList-header">
                    { renderHeader() }
                </thead>
                <tbody className="veneList-body">
                    { renderBody() }
                </tbody>
            </table>
        </div>
    )
}